import { GlobalStyles } from "@mui/material";
import { Box } from "@mui/system";

import { EVOTRUX_SURFACE_WHITE } from "constants/index";
import { MAIN_CONTENT_ELEMENT_ID } from "layouts/constants";

interface Props {
  dashboard: React.ReactNode;
}

/**
 * Page shell for an embedded analytics dashboard. Provider-neutral, so it does
 * not depend on any one analytics vendor's component tree.
 *
 * Deliberately has no page title: the dashboard names itself, and a heading
 * above it only pushed the content down and duplicated the nav.
 */
export function EmbeddedAnalyticsPage(props: Props) {
  const { dashboard } = props;

  return (
    <>
      {/* The app shell scrolls with `overflow-y: scroll`, so it paints a
          scrollbar track even on a viewport-fit page like this one — next to
          the dashboard's own scrollbar, that reads as two side by side. This
          page owns its scrolling, so the shell's is suppressed while it is
          mounted, and restored for every other page on unmount. */}
      <GlobalStyles
        styles={{
          [`#${MAIN_CONTENT_ELEMENT_ID}`]: { overflowY: "hidden" },
        }}
      />
      <Box
        id="embedded-analytics-page"
        sx={{
          backgroundColor: EVOTRUX_SURFACE_WHITE,
          // The app shell expects viewport-fit pages, so the dashboard region
          // below owns the scrolling. Attempts to let this surface grow with the
          // ~14000px dashboard fought the shell's flex sizing and regressed
          // repeatedly.
          height: "100%",
          width: "100%",
          flex: 1,
          borderRadius: 4,
          overflow: "hidden",
        }}
      >
        {/* The scroll container: the embed renders its full natural height
            inside and scrolls here, keeping the page itself viewport-fit.

            Horizontal scrolling is allowed as well, because a provider can
            render wider than this container and there is no way back from
            that. Luzmo sizes a dashboard to the screen mode it picked and
            clamps the width up to that mode's minimum
            (`Math.max(Math.min(maxWidth, containerWidth), minWidth)` in
            @luzmo/embed), so a container sitting just inside the bottom of a
            band gets a canvas wider than itself. Clipping it hid the right
            edge with no scrollbar and nothing reported. */}
        <Box sx={{ height: "100%", overflow: "auto" }}>{dashboard}</Box>
      </Box>
    </>
  );
}
